import React, {
    useCallback, useContext, useEffect, useMemo, useState
} from 'react'
import { useDispatch } from 'react-redux'
import { Form, Formik, FormikProps } from 'formik'
import { _, withCapture } from 'utils'
import FullPageLoading from 'components/FullPageLoading'
import { LayoutContext } from 'components/layout/Layout'
import { S } from 'features/forms/FormTabs'
import DateField from 'features/forms/components/DateField'
import Editor from 'features/forms/components/Editor'
import TextField from 'features/forms/components/TextField'
import TimeField from 'features/forms/components/TimeField'
import { showNotification } from 'features/notifications'
import { updateGroupSession } from '../graphql/mutation'
import { GroupSessionContext, GroupSessionValidation } from '../GroupSessionEditor'

const ChangeWatcher = ({ hasChange }: { hasChange: boolean }) => {
    const { setFormChanged } = useContext(LayoutContext)

    useEffect(() => {
        setFormChanged(hasChange)
    }, [hasChange, setFormChanged])

    return null
}

const Settings = () => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const { groupSession, refresh } = useContext(GroupSessionContext)
    const { screenWidth, scaledSidebar } = useContext(LayoutContext)
    const initValues = useMemo(() => ({
        title: groupSession.title,
        description: groupSession.description,
        startsAt: new Date(groupSession.startsAt),
        endsAt: new Date(groupSession.endsAt),
    }), [groupSession])

    const handleSubmit = useCallback(async (values, { resetForm }) => {
        setLoading(true)
        const response = await withCapture(
            async () => await updateGroupSession(groupSession.id, { ...values })
        )()
        setLoading(false)
        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: _('form.notification.update_success'),
            }))
            resetForm({ values })
            refresh()
        }
    }, [dispatch, groupSession.id, refresh])

    if (!groupSession) {
        return
    }

    if (loading) {
        return <FullPageLoading />
    }

    return (
        <Formik
            initialValues={initValues || {}}
            onSubmit={handleSubmit}
            validationSchema={GroupSessionValidation}
        >
            {(props: FormikProps<any>) => (
                <Form className="row">
                    <TextField name="title" isRequired label={_('groupsession.form.title')} />
                    <Editor name="description" label={_('groupsession.form.description')} />
                    <DateField
                        name="startsAt"
                        label={_('groupsession.form.start_date')}
                        lg={1/2}
                        isRequired
                    />
                    <TimeField
                        name="startsAt"
                        label={_('groupsession.form.start_time')}
                        lg={1/2}
                        isRequired
                    />
                    <DateField
                        name="endsAt"
                        label={_('groupsession.form.end_date')}
                        lg={1/2}
                        isRequired
                    />
                    <TimeField
                        name="endsAt"
                        label={_('groupsession.form.end_time')}
                        lg={1/2}
                        isRequired
                    />
                    <ChangeWatcher hasChange={props.isValid && props.dirty} />
                    {(props.isValid && props.dirty) && (
                        <S.FormActions screenWidth={screenWidth} scaledSidebar={scaledSidebar}>
                            <S.ActionDescription>
                                {_('form.submit_description')}
                            </S.ActionDescription>
                            <S.SaveButton
                                size="sm"
                                disabled={!props.isValid || !props.dirty}
                                variant="primary"
                                icon="save"
                                onClick={props.handleSubmit}
                                type="submit"
                            >
                                {_('form.submit_button')}
                            </S.SaveButton>
                        </S.FormActions>
                    )}
                </Form>
            )}
        </Formik>
    )
}

export default Settings
